import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Row, Col, FormGroup, Label, Input, Button, Spinner, Alert,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from 'reactstrap'
import moment from 'moment'
import Select from '../../../../components/Template/Input/Select'
import oikosApi from '../../../../services/oikos'
import { selectValues, getRegs } from '../../../../utils/fc'
import { notifySuccess, notifyError } from '../../../../utils/notify'
import qs from 'qs'

const UF_OPTIONS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
].map(uf => ({ value: uf, label: uf }))

const LOGO_DESC = 'case-logo'
const FOTO_DESC_PREFIX = 'case-foto:'
const ARQUIVO_TAB_REF = 'projCaseTeste'

const resolveUfOption = (raw) => {
  if (raw == null || raw === '') return null
  const s = String(raw).trim().toUpperCase()
  return UF_OPTIONS.find(o => o.value === s) || null
}

const parseServicoIds = (raw) => {
  if (!raw) return []
  if (Array.isArray(raw)) return raw.map(Number).filter(n => !Number.isNaN(n))
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        return parsed.map((item) => {
          if (item && typeof item === 'object' && item.value != null) return Number(item.value)
          return Number(item)
        }).filter(n => !Number.isNaN(n))
      }
    } catch (e) {
      return []
    }
  }
  return []
}

const nowSql = () => {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

const oikosFileUrl = (caminho) => {
  if (!caminho) return ''
  if (/^https?:\/\//i.test(caminho)) return caminho
  // Arquivos ficam no mesmo host da API (prod), não no front beta
  const api = process.env.REACT_APP_OIKOS_API_URL || 'https://oikos.ecoss.is/oikos/_oikos_api.php'
  const base = api.replace(/\/_oikos_api\.php.*$/i, '/').replace(/\/?$/, '/')
  const path = String(caminho).replace(/^\//, '')
  return `${base}${path}`
}

/** Parse resposta do upload_file (mesmo estilo ofícios / solicitação jurídica). */
const parseUploadArquivo = (uploadRes) => {
  const resStr = typeof uploadRes?.data === 'string'
    ? uploadRes.data
    : JSON.stringify(uploadRes?.data ?? '')
  const match = resStr.match(/\[\{"arquivo_id".*?\}\]/)
  if (!match) {
    if (Array.isArray(uploadRes?.data) && uploadRes.data[0]?.arquivo_id) {
      return uploadRes.data[0]
    }
    return null
  }
  try {
    const parsed = JSON.parse(match[0])
    return parsed?.[0] || null
  } catch (e) {
    return null
  }
}

/** Upload direto em `arquivo` (padrão ofícios criados/recebidos). */
const uploadArquivoDireto = async ({ tabId, file, arquivoDesc }) => {
  const fileName = file?.name || `arquivo-${Date.now()}`
  const formData = new FormData()
  formData.append('file', file, fileName)
  formData.append('table', 'arquivo')
  formData.append('action', 'upload_file')
  formData.append('arquivo_tabRef', ARQUIVO_TAB_REF)
  formData.append('arquivo_tabId', tabId)
  formData.append('arquivo_desc', arquivoDesc || fileName)
  formData.append('arquivo_privacidade', '1')

  const uploadRes = await oikosApi.post('/', formData)
  const parsed = parseUploadArquivo(uploadRes)
  if (!parsed?.arquivo_id) {
    throw new Error('Upload sem retorno de arquivo_id')
  }
  return parsed
}

const softDeleteArquivo = async (arquivoId) => {
  if (!arquivoId) return
  await oikosApi.post('/', {
    action: 'update',
    table: 'arquivo',
    data: {
      arquivo_id: arquivoId,
      arquivo_ativo: 0,
    },
  })
}

const fotoOrdemFromDesc = (desc) => {
  const m = String(desc || '').match(/^case-foto:(\d+)/)
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER
}

const thumbStyle = {
  width: 96,
  height: 96,
  objectFit: 'cover',
  borderRadius: 6,
  border: '1px solid #dee2e6',
}

const emptyForm = () => ({
  titulo: '',
  resumo: '',
  desafio: '',
  servicos: [],
  areaAtua: null,
  anoAssinatura: '',
  cidade: '',
  estado: null,
  depoimentoStake: null,
  depoimentoTexto: '',
})

const isLinkActive = (row) => row && Number(row.projCaseTeste_ativo) === 1

const formatApiError = (err) => {
  const d = err?.data
  if (typeof d === 'string' && d.trim()) return d.trim()
  if (d?.message) return String(d.message)
  if (d?.error) return String(d.error)
  if (Array.isArray(d) && d[0]) return String(d[0])
  if (err?.message) return err.message
  return null
}

/** PHP insertData/updateData concatena sem escape — dobrar ' evita SQL quebrado (ex.: D'Ávila). */
const escapeOikosSqlPayload = (data) => {
  const out = {}
  Object.keys(data || {}).forEach((key) => {
    const value = data[key]
    out[key] = typeof value === 'string' ? value.replace(/'/g, "''") : value
  })
  return out
}

const findActiveLink = (rows, predicate) => {
  if (!Array.isArray(rows)) return null
  return rows.find(row => isLinkActive(row) && predicate(row)) || null
}

const findInactiveLink = (rows, predicate) => {
  if (!Array.isArray(rows)) return null
  return rows.find(row => !isLinkActive(row) && predicate(row)) || null
}

const MarketingCaseForm = ({ project }) => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [servicoOptions, setServicoOptions] = useState([])
  const [areaAtuaOptions, setAreaAtuaOptions] = useState([])
  const [stakeholderOptions, setStakeholderOptions] = useState([])

  const [caseRow, setCaseRow] = useState(null)
  const [wpMeta, setWpMeta] = useState(null)

  const [linkModalOpen, setLinkModalOpen] = useState(false)
  const [linkLoading, setLinkLoading] = useState(false)
  const [linkSaving, setLinkSaving] = useState(false)
  const [wpCaseOptions, setWpCaseOptions] = useState([])
  const [selectedWpCase, setSelectedWpCase] = useState(null)
  const [allCaseLinks, setAllCaseLinks] = useState([])
  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false)
  const [unlinkSaving, setUnlinkSaving] = useState(false)

  const [titulo, setTitulo] = useState('')
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('')
  const [logoArquivoId, setLogoArquivoId] = useState(null)
  const [resumo, setResumo] = useState('')
  const [desafio, setDesafio] = useState('')
  const [servicos, setServicos] = useState([])
  const [areaAtua, setAreaAtua] = useState(null)
  const [anoAssinatura, setAnoAssinatura] = useState('')
  const [cidade, setCidade] = useState('')
  const [estado, setEstado] = useState(null)
  const [fotos, setFotos] = useState([])
  const [depoimentoStake, setDepoimentoStake] = useState(null)
  const [depoimentoTexto, setDepoimentoTexto] = useState('')

  const objectUrlsRef = useRef([])
  const trackUrl = useCallback((url) => {
    if (url) objectUrlsRef.current.push(url)
    return url
  }, [])

  const clearMediaState = useCallback(() => {
    setLogoFile(null)
    setLogoPreviewUrl('')
    setLogoArquivoId(null)
    setFotos([])
  }, [])

  const applyForm = useCallback((patch) => {
    const next = { ...emptyForm(), ...patch }
    setTitulo(next.titulo)
    setResumo(next.resumo)
    setDesafio(next.desafio)
    setServicos(next.servicos)
    setAreaAtua(next.areaAtua)
    setAnoAssinatura(next.anoAssinatura)
    setCidade(next.cidade)
    setEstado(next.estado)
    setDepoimentoStake(next.depoimentoStake)
    setDepoimentoTexto(next.depoimentoTexto)
  }, [])

  const loadMidia = useCallback(async (caseId) => {
    if (!caseId) {
      clearMediaState()
      return
    }
    const rows = await getRegs('arquivo', {
      arquivo_tabRef: ARQUIVO_TAB_REF,
      arquivo_tabId: caseId,
    })
    const list = Array.isArray(rows) ? rows : []

    const logoRow = list.find(a => String(a.arquivo_desc || '') === LOGO_DESC)
      || list.find(a => String(a.arquivo_desc || '').startsWith(LOGO_DESC))
    if (logoRow?.arquivo_caminho) {
      setLogoArquivoId(logoRow.arquivo_id)
      setLogoFile(null)
      setLogoPreviewUrl(oikosFileUrl(logoRow.arquivo_caminho))
    } else {
      setLogoArquivoId(null)
      setLogoFile(null)
      setLogoPreviewUrl('')
    }

    const fotoRows = list
      .filter(a => String(a.arquivo_desc || '').startsWith(FOTO_DESC_PREFIX))
      .sort((a, b) => {
        const oa = fotoOrdemFromDesc(a.arquivo_desc)
        const ob = fotoOrdemFromDesc(b.arquivo_desc)
        if (oa !== ob) return oa - ob
        return Number(a.arquivo_id) - Number(b.arquivo_id)
      })

    setFotos(fotoRows.map((a, idx) => ({
      id: `saved-${a.arquivo_id}`,
      arquivo_id: a.arquivo_id,
      previewUrl: oikosFileUrl(a.arquivo_caminho),
      caminho: a.arquivo_caminho || null,
      name: a.arquivo_caminho
        ? String(a.arquivo_caminho).split('/').pop()
        : `Foto ${idx + 1}`,
      file: null,
      ordem: fotoOrdemFromDesc(a.arquivo_desc),
    })))
  }, [clearMediaState])

  const load = useCallback(async () => {
    if (!project?.proj_id) {
      setCaseRow(null)
      setWpMeta(null)
      applyForm(emptyForm())
      clearMediaState()
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const [servicosSel, areas, stakes, links] = await Promise.all([
        selectValues('servico', 'servico_id', 'servico_nome'),
        selectValues('areaAtua', 'areaAtua_id', 'areaAtua_area'),
        getRegs('projStake', { proj_id: project.proj_id }, {
          '(SELECT pf_nomeExibicao FROM pf WHERE pf.pf_id = projStake.pf_id)': 'pf_nomeExibicao',
          '(SELECT papel_nome FROM papel WHERE papel.papel_id = projStake.papel_id)': 'papel_nome',
        }),
        getRegs('projCaseTeste', { proj_id: project.proj_id }),
      ])

      const servOpts = Array.isArray(servicosSel) ? servicosSel : []
      const areaOpts = Array.isArray(areas) ? areas : []
      const stakeOpts = (Array.isArray(stakes) ? stakes : []).map(s => ({
        value: s.projStake_id,
        label: s.papel_nome
          ? `${s.pf_nomeExibicao || '—'} (${s.papel_nome})`
          : (s.pf_nomeExibicao || `Stake #${s.projStake_id}`),
        pf_nomeExibicao: s.pf_nomeExibicao,
      }))

      setServicoOptions(servOpts)
      setAreaAtuaOptions(areaOpts)
      setStakeholderOptions(stakeOpts)

      const link = Array.isArray(links) && links.length ? links[0] : null
      setCaseRow(link)

      let wp = null
      if (link?.wordpress_id) {
        try {
          const wpRes = await oikosApi.get(`/?${qs.stringify({
            action: 'get',
            table: 'import_wordpress_projects',
            fields: {
              wordpress_id: 'wordpress_id',
              title: 'title',
              permalink: 'permalink',
              client_name: 'client_name',
            },
            data: { wordpress_id: link.wordpress_id },
          })}`)
          wp = Array.isArray(wpRes.data) && wpRes.data.length ? wpRes.data[0] : null
        } catch (e) {
          console.warn('MarketingCaseForm WP meta:', e)
        }
      }
      setWpMeta(wp)

      if (link) {
        const servicoIds = parseServicoIds(link.projCaseTeste_servico)
        applyForm({
          titulo: link.projCaseTeste_titulo || link.projCaseTeste_nome || '',
          resumo: link.projCaseTeste_resumo || '',
          desafio: link.projCaseTeste_desafio || '',
          servicos: servicoIds
            .map(id => servOpts.find(o => Number(o.value) === Number(id)))
            .filter(Boolean),
          areaAtua: link.areaAtua_id != null
            ? (areaOpts.find(o => Number(o.value) === Number(link.areaAtua_id)) || {
              value: link.areaAtua_id,
              label: `Setor #${link.areaAtua_id}`,
            })
            : null,
          anoAssinatura: link.projCaseTeste_ano != null ? String(link.projCaseTeste_ano) : '',
          cidade: link.projCaseTeste_cidade || '',
          estado: resolveUfOption(link.projCaseTeste_uf),
          depoimentoStake: link.projCaseTeste_depoimentoProjStake_id != null
            ? (stakeOpts.find(o => Number(o.value) === Number(link.projCaseTeste_depoimentoProjStake_id)) || null)
            : null,
          depoimentoTexto: link.projCaseTeste_depoimentoTexto || '',
        })
        await loadMidia(link.projCaseTeste_id)
      } else {
        const defaults = emptyForm()
        if (project?.contrato_dtAssin) {
          const year = moment(project.contrato_dtAssin).year()
          if (year) defaults.anoAssinatura = String(year)
        }
        applyForm(defaults)
        clearMediaState()
      }
    } catch (err) {
      console.error('MarketingCaseForm load error:', err)
      notifyError('Não foi possível carregar o Case do projeto.')
      setCaseRow(null)
      setWpMeta(null)
      clearMediaState()
    } finally {
      setLoading(false)
    }
  }, [project?.proj_id, project?.contrato_dtAssin, applyForm, clearMediaState, loadMidia])

  useEffect(() => {
    load()
    return () => {
      objectUrlsRef.current.forEach((url) => {
        try { URL.revokeObjectURL(url) } catch (e) { /* ignore */ }
      })
      objectUrlsRef.current = []
    }
  }, [load])

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0] || null
    if (logoPreviewUrl && logoFile) {
      try { URL.revokeObjectURL(logoPreviewUrl) } catch (err) { /* ignore */ }
    }
    if (!file) {
      setLogoFile(null)
      return
    }
    if (!file.type.startsWith('image/')) {
      notifyError('O logo deve ser uma imagem.')
      e.target.value = ''
      return
    }
    const url = trackUrl(URL.createObjectURL(file))
    setLogoFile(file)
    setLogoPreviewUrl(url)
  }

  const handleLogoClear = async () => {
    if (logoFile && logoPreviewUrl) {
      try { URL.revokeObjectURL(logoPreviewUrl) } catch (e) { /* ignore */ }
    }
    if (logoArquivoId) {
      try {
        await softDeleteArquivo(logoArquivoId)
        notifySuccess('Logo removido.')
      } catch (err) {
        console.error(err)
        notifyError('Não foi possível remover o logo.')
        return
      }
    }
    setLogoFile(null)
    setLogoPreviewUrl('')
    setLogoArquivoId(null)
  }

  const handleFotosAdd = (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (!files.length) return

    const next = []
    files.forEach((file) => {
      if (!file.type.startsWith('image/')) return
      const previewUrl = trackUrl(URL.createObjectURL(file))
      next.push({
        id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        previewUrl,
        name: file.name,
        arquivo_id: null,
      })
    })
    if (!next.length) {
      notifyError('Selecione apenas imagens para o carrossel.')
      return
    }
    setFotos(prev => [...prev, ...next])
  }

  const removeFoto = async (id) => {
    const item = fotos.find(f => f.id === id)
    if (!item) return

    if (item.arquivo_id) {
      try {
        await softDeleteArquivo(item.arquivo_id)
      } catch (err) {
        console.error(err)
        notifyError('Não foi possível remover a foto.')
        return
      }
    }
    if (item.file && item.previewUrl) {
      try { URL.revokeObjectURL(item.previewUrl) } catch (e) { /* ignore */ }
    }
    setFotos(prev => prev.filter(f => f.id !== id))
  }

  const handleSave = async () => {
    if (!caseRow?.projCaseTeste_id) {
      notifyError('Vincule um case WordPress a este projeto antes de salvar.')
      return
    }

    const caseId = caseRow.projCaseTeste_id
    setSaving(true)
    try {
      const anoNum = anoAssinatura ? Number(anoAssinatura) : null
      const payload = {
        projCaseTeste_id: caseId,
        projCaseTeste_titulo: titulo || null,
        projCaseTeste_nome: titulo || caseRow.projCaseTeste_nome || null,
        projCaseTeste_resumo: resumo || null,
        projCaseTeste_desafio: desafio || null,
        projCaseTeste_servico: JSON.stringify((servicos || []).map(s => s.value)),
        areaAtua_id: areaAtua?.value != null ? Number(areaAtua.value) : null,
        projCaseTeste_ano: Number.isFinite(anoNum) ? anoNum : null,
        projCaseTeste_cidade: String(cidade || '').trim().slice(0, 128) || null,
        projCaseTeste_uf: estado?.value ? String(estado.value).slice(0, 2) : null,
        projCaseTeste_depoimentoProjStake_id: depoimentoStake?.value != null
          ? Number(depoimentoStake.value)
          : null,
        projCaseTeste_depoimentoTexto: depoimentoTexto || null,
        projCaseTeste_dtMod: nowSql(),
      }

      const res = await oikosApi.post('/', {
        action: 'update',
        table: 'projCaseTeste',
        data: escapeOikosSqlPayload(payload),
      })
      if (!res?.ok && res?.problem) {
        throw res
      }

      // Logo (mesmo fluxo de anexo de ofício)
      if (logoFile) {
        if (logoArquivoId) {
          await softDeleteArquivo(logoArquivoId)
        }
        await uploadArquivoDireto({
          tabId: caseId,
          file: logoFile,
          arquivoDesc: LOGO_DESC,
        })
      }

      // Carrossel: ordem em arquivo_desc (case-foto:N), na ordem da tela
      let fotoIdx = 0
      for (const foto of fotos) {
        const desc = `${FOTO_DESC_PREFIX}${fotoIdx}`
        if (foto.file) {
          await uploadArquivoDireto({
            tabId: caseId,
            file: foto.file,
            arquivoDesc: desc,
          })
        } else if (foto.arquivo_id) {
          await oikosApi.post('/', {
            action: 'update',
            table: 'arquivo',
            data: {
              arquivo_id: foto.arquivo_id,
              arquivo_desc: desc,
            },
          })
        }
        fotoIdx += 1
      }

      notifySuccess('Case salvo.')
      await load()
    } catch (err) {
      console.error('MarketingCaseForm save error:', err)
      notifyError('Não foi possível salvar o Case.')
    } finally {
      setSaving(false)
    }
  }

  const openLinkModal = useCallback(async () => {
    if (!project?.proj_id) return
    setLinkModalOpen(true)
    setSelectedWpCase(null)
    setLinkLoading(true)
    try {
      const [wpRes, allLinks] = await Promise.all([
        oikosApi.get(`/?${qs.stringify({
          action: 'get',
          table: 'import_wordpress_projects',
          fields: {
            wordpress_id: 'wordpress_id',
            title: 'title',
            client_name: 'client_name',
            permalink: 'permalink',
            start_year: 'start_year',
            location_raw: 'location_raw',
            state_raw: 'state_raw',
            excerpt: 'excerpt',
            description_text: 'description_text',
          },
        })}`),
        // inclui inativos — UNIQUE em proj_id/wordpress_id exige reaproveitar linha soft-deleted
        getRegs('projCaseTeste', {}, {
          projCaseTeste_id: 'projCaseTeste_id',
          proj_id: 'proj_id',
          wordpress_id: 'wordpress_id',
          projCaseTeste_ativo: 'projCaseTeste_ativo',
        }, false),
      ])

      const wpRows = Array.isArray(wpRes?.data) ? wpRes.data : []
      const links = Array.isArray(allLinks) ? allLinks : []
      setAllCaseLinks(links)

      const activeWp = new Set(
        links
          .filter(isLinkActive)
          .map(l => Number(l.wordpress_id))
          .filter(n => !Number.isNaN(n)),
      )

      const opts = wpRows
        .filter(w => w.wordpress_id != null && !activeWp.has(Number(w.wordpress_id)))
        .map(w => ({
          value: Number(w.wordpress_id),
          label: w.client_name
            ? `${w.title || `Case #${w.wordpress_id}`} — ${w.client_name}`
            : (w.title || `Case #${w.wordpress_id}`),
          raw: w,
        }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label), 'pt-BR'))

      setWpCaseOptions(opts)
    } catch (err) {
      console.error('MarketingCaseForm openLinkModal:', err)
      notifyError('Não foi possível carregar os cases do WordPress.')
      setWpCaseOptions([])
      setAllCaseLinks([])
    } finally {
      setLinkLoading(false)
    }
  }, [project?.proj_id])

  const confirmLinkCase = useCallback(async () => {
    const wp = selectedWpCase?.raw
    const projId = Number(project?.proj_id)
    const wpId = Number(selectedWpCase?.value)
    if (!projId || !wpId || !wp) {
      notifyError('Selecione um case para vincular.')
      return
    }

    setLinkSaving(true)
    try {
      const projConflict = findActiveLink(
        allCaseLinks,
        l => l.proj_id != null && Number(l.proj_id) === projId,
      )
      if (projConflict) {
        notifyError('Este projeto já possui um case vinculado.')
        return
      }

      const wpConflict = findActiveLink(
        allCaseLinks,
        l => l.wordpress_id != null && Number(l.wordpress_id) === wpId,
      )
      if (wpConflict) {
        notifyError(`Case WP ${wpId} já está vinculado ao projeto #${wpConflict.proj_id}.`)
        return
      }

      const inactiveRow = findInactiveLink(
        allCaseLinks,
        l => (l.wordpress_id != null && Number(l.wordpress_id) === wpId)
          || (l.proj_id != null && Number(l.proj_id) === projId),
      )

      const cidade = String(wp.location_raw || '').trim().slice(0, 128) || null
      const uf = resolveUfOption(wp.state_raw)?.value || null
      const descricao = wp.description_text || wp.excerpt || null

      const payload = {
        proj_id: projId,
        wordpress_id: wpId,
        projCaseTeste_nome: wp.title || `Case ${wpId}`,
        projCaseTeste_titulo: wp.title || null,
        projCaseTeste_resumo: descricao,
        projCaseTeste_ano: wp.start_year || null,
        projCaseTeste_cidade: cidade,
        projCaseTeste_uf: uf,
        projCaseTeste_servico: JSON.stringify([]),
        projCaseTeste_ativo: 1,
        projCaseTeste_dtMod: nowSql(),
      }

      if (inactiveRow?.projCaseTeste_id) {
        const res = await oikosApi.post('/', {
          action: 'update',
          table: 'projCaseTeste',
          data: escapeOikosSqlPayload({
            ...payload,
            projCaseTeste_id: inactiveRow.projCaseTeste_id,
          }),
        })
        if (!res?.ok && res?.problem) throw res
      } else {
        const res = await oikosApi.post('/', {
          action: 'insert',
          table: 'projCaseTeste',
          data: escapeOikosSqlPayload({
            ...payload,
            projCaseTeste_dtCria: nowSql(),
          }),
        })
        const savedId = res?.data?.[0]?.projCaseTeste_id
        if (!savedId) {
          throw new Error(formatApiError(res) || 'Insert sem retorno de ID')
        }
      }

      notifySuccess(inactiveRow ? 'Case revinculado ao projeto.' : 'Case vinculado ao projeto.')
      setLinkModalOpen(false)
      setSelectedWpCase(null)
      await load()
    } catch (err) {
      console.error('MarketingCaseForm confirmLinkCase:', err)
      const detail = formatApiError(err)
      notifyError(
        detail
          ? `Não foi possível vincular o case: ${detail}`
          : 'Não foi possível vincular o case.',
      )
    } finally {
      setLinkSaving(false)
    }
  }, [selectedWpCase, project?.proj_id, allCaseLinks, load])

  const confirmUnlinkCase = useCallback(async () => {
    const id = caseRow?.projCaseTeste_id
    if (!id) return

    setUnlinkSaving(true)
    try {
      let cleared = false
      try {
        const res = await oikosApi.post('/', {
          action: 'update',
          table: 'projCaseTeste',
          data: {
            projCaseTeste_id: id,
            proj_id: null,
            wordpress_id: null,
            areaAtua_id: null,
            projCaseTeste_ativo: 0,
            projCaseTeste_dtMod: nowSql(),
          },
        })
        if (!res?.ok && res?.problem) throw res
        cleared = true
      } catch (clearErr) {
        console.warn('MarketingCaseForm unlink clear keys failed, trying soft delete', clearErr)
      }

      if (!cleared) {
        const res = await oikosApi.post('/', {
          action: 'delete',
          table: 'projCaseTeste',
          data: { projCaseTeste_id: id },
        })
        if (!res?.ok && res?.problem) throw res
      }

      const stillActive = await getRegs('projCaseTeste', { projCaseTeste_id: id })
      if (Array.isArray(stillActive) && stillActive.length > 0) {
        throw new Error('O registro continua ativo após desvincular.')
      }

      notifySuccess('Case desvinculado.')
      setUnlinkConfirmOpen(false)
      await load()
    } catch (err) {
      console.error('MarketingCaseForm confirmUnlinkCase:', err)
      const detail = formatApiError(err)
      notifyError(
        detail
          ? `Não foi possível desvincular: ${detail}`
          : 'Não foi possível desvincular o case.',
      )
    } finally {
      setUnlinkSaving(false)
    }
  }, [caseRow?.projCaseTeste_id, load])

  if (loading) {
    return (
      <div className="d-flex align-items-center text-muted py-3">
        <Spinner size="sm" className="mr-2" /> Carregando formulário do Case...
      </div>
    )
  }

  if (!caseRow) {
    return (
      <>
        <Alert color="light" className="border mb-0">
          <div className="d-flex flex-wrap align-items-center justify-content-between">
            <span className="mb-2 mb-sm-0">Nenhum case encontrado para este projeto.</span>
            <Button color="primary" size="sm" onClick={openLinkModal}>
              Vincular case
            </Button>
          </div>
        </Alert>

        <Modal isOpen={linkModalOpen} toggle={() => !linkSaving && setLinkModalOpen(false)} size="lg">
          <ModalHeader toggle={() => !linkSaving && setLinkModalOpen(false)}>
            Vincular case ao projeto
          </ModalHeader>
          <ModalBody>
            {linkLoading ? (
              <div className="d-flex align-items-center text-muted py-3">
                <Spinner size="sm" className="mr-2" /> Carregando cases...
              </div>
            ) : (
              <FormGroup className="mb-0">
                <Label>Case WordPress</Label>
                <Select
                  options={wpCaseOptions}
                  value={selectedWpCase}
                  onChange={setSelectedWpCase}
                  placeholder="Buscar por título ou cliente..."
                  isClearable
                  isDisabled={linkSaving}
                />
                {!wpCaseOptions.length && (
                  <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                    Não há cases disponíveis para vincular (todos já estão ligados a projetos, ou a importação WP está vazia).
                  </div>
                )}
              </FormGroup>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" disabled={linkSaving} onClick={() => setLinkModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              color="primary"
              disabled={linkSaving || linkLoading || !selectedWpCase}
              onClick={confirmLinkCase}
            >
              {linkSaving ? <Spinner size="sm" /> : 'Confirmar vinculação'}
            </Button>
          </ModalFooter>
        </Modal>
      </>
    )
  }

  return (
    <div>
      <Alert color="info" className="py-2">
        <div className="d-flex flex-wrap align-items-start justify-content-between">
          <div>
            <div style={{ fontSize: 13 }}>
              Case vinculado
              {caseRow.wordpress_id != null && (
                <>
                  {' '}· WP ID <strong>{caseRow.wordpress_id}</strong>
                </>
              )}
              {wpMeta?.title && (
                <>
                  {' '}· <strong>{wpMeta.title}</strong>
                </>
              )}
              {wpMeta?.client_name && (
                <span className="text-muted"> ({wpMeta.client_name})</span>
              )}
            </div>
            {wpMeta?.permalink && (
              <div style={{ fontSize: 12 }}>
                <a href={wpMeta.permalink} target="_blank" rel="noopener noreferrer">
                  Abrir case no site
                </a>
              </div>
            )}
          </div>
          <Button
            color="danger"
            outline
            size="sm"
            className="mt-1 mt-sm-0 ml-sm-2"
            disabled={saving || unlinkSaving}
            onClick={() => setUnlinkConfirmOpen(true)}
          >
            Desvincular
          </Button>
        </div>
      </Alert>

      <Modal
        isOpen={unlinkConfirmOpen}
        toggle={() => !unlinkSaving && setUnlinkConfirmOpen(false)}
        centered
      >
        <ModalHeader toggle={() => !unlinkSaving && setUnlinkConfirmOpen(false)}>
          Confirmar desvinculação
        </ModalHeader>
        <ModalBody>
          Tem certeza que deseja desvincular este case do projeto?
          {wpMeta?.title && (
            <div className="mt-2 text-muted" style={{ fontSize: 13 }}>
              Case: <strong>{wpMeta.title}</strong>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            disabled={unlinkSaving}
            onClick={() => setUnlinkConfirmOpen(false)}
          >
            Cancelar
          </Button>
          <Button color="danger" disabled={unlinkSaving} onClick={confirmUnlinkCase}>
            {unlinkSaving ? <Spinner size="sm" /> : 'Confirmar desvinculação'}
          </Button>
        </ModalFooter>
      </Modal>

      <Row>
        <Col md={8}>
          <FormGroup>
            <Label>Título do case</Label>
            <Input
              value={titulo}
              onChange={e => setTitulo(e.target.value)}
              placeholder="Título do case"
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>Ano da assinatura do projeto / contrato</Label>
            <Input
              type="number"
              min="1990"
              max="2100"
              value={anoAssinatura}
              onChange={e => setAnoAssinatura(e.target.value)}
              placeholder="Ex.: 2024"
            />
          </FormGroup>
        </Col>
      </Row>

      <FormGroup>
        <Label>Logo do case / cliente</Label>
        <Input type="file" accept="image/*" onChange={handleLogoChange} />
        {logoPreviewUrl && (
          <div className="mt-2 d-flex align-items-start" style={{ gap: 12 }}>
            <a href={logoPreviewUrl} target="_blank" rel="noopener noreferrer" title="Abrir logo">
              <img src={logoPreviewUrl} alt="Logo do case" style={{ ...thumbStyle, width: 120, height: 120 }} />
            </a>
            <div>
              {(logoFile?.name || logoArquivoId) && (
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {logoFile?.name || 'Logo salvo'}
                </div>
              )}
              {!logoFile && logoPreviewUrl && (
                <div>
                  <a href={logoPreviewUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                    Abrir logo
                  </a>
                </div>
              )}
              <Button
                color="link"
                size="sm"
                className="p-0 text-danger"
                disabled={saving}
                onClick={handleLogoClear}
              >
                Remover logo
              </Button>
            </div>
          </div>
        )}
      </FormGroup>

      <FormGroup>
        <Label>Resumo</Label>
        <Input
          type="textarea"
          rows={3}
          value={resumo}
          onChange={e => setResumo(e.target.value)}
          placeholder="Resumo do case"
        />
      </FormGroup>

      <FormGroup>
        <Label>Desafio</Label>
        <Input
          type="textarea"
          rows={3}
          value={desafio}
          onChange={e => setDesafio(e.target.value)}
          placeholder="Desafio enfrentado"
        />
      </FormGroup>

      <FormGroup>
        <Label>Setor</Label>
        <Select
          classNamePrefix="select2-selection"
          isSearchable
          isClearable
          placeholder="Selecionar setor..."
          options={areaAtuaOptions}
          value={areaAtua}
          onChange={opt => setAreaAtua(opt)}
        />
      </FormGroup>

      <FormGroup>
        <Label>Serviços</Label>
        <Select
          classNamePrefix="select2-selection"
          isMulti
          isSearchable
          isClearable
          placeholder="Selecionar serviços..."
          options={servicoOptions}
          value={servicos}
          onChange={opts => setServicos(opts || [])}
        />
      </FormGroup>

      <Row>
        <Col md={8}>
          <FormGroup>
            <Label>Cidade</Label>
            <Input
              value={cidade}
              onChange={e => setCidade(e.target.value)}
              placeholder="Cidade onde o projeto ocorre"
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>Estado</Label>
            <Select
              classNamePrefix="select2-selection"
              isClearable
              isSearchable
              placeholder="UF"
              options={UF_OPTIONS}
              value={estado}
              onChange={opt => setEstado(opt)}
            />
          </FormGroup>
        </Col>
      </Row>

      <FormGroup>
        <Label>Carrossel de fotos</Label>
        <Input type="file" accept="image/*" multiple onChange={handleFotosAdd} />
        {fotos.length > 0 && (
          <div className="d-flex flex-wrap mt-2" style={{ gap: 12 }}>
            {fotos.map(foto => (
              <div key={foto.id} style={{ position: 'relative', width: 96 }}>
                {foto.previewUrl ? (
                  <a
                    href={foto.previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Abrir foto"
                  >
                    <img
                      src={foto.previewUrl}
                      alt={foto.name || 'foto'}
                      style={thumbStyle}
                    />
                  </a>
                ) : (
                  <div
                    style={{
                      ...thumbStyle,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#f1f3f5',
                      fontSize: 11,
                      color: '#868e96',
                    }}
                  >
                    Sem URL
                  </div>
                )}
                <Button
                  color="danger"
                  size="sm"
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    padding: '0 6px',
                    lineHeight: '18px',
                  }}
                  disabled={saving}
                  onClick={() => removeFoto(foto.id)}
                  title="Remover foto"
                >
                  ×
                </Button>
                <div
                  className="text-muted text-truncate"
                  style={{ fontSize: 11 }}
                  title={foto.name || foto.file?.name}
                >
                  {foto.file ? 'Nova' : 'Salva'}
                </div>
                {foto.previewUrl && !foto.file && (
                  <a
                    href={foto.previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 11 }}
                  >
                    Abrir
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="text-muted" style={{ fontSize: 12 }}>
          Fotos novas são gravadas ao clicar em Salvar Case.
        </div>
      </FormGroup>

      <hr />

      <h6 className="mb-3">Depoimento</h6>
      <p className="text-muted" style={{ fontSize: 13 }}>
        Associado a um stakeholder do projeto (nome da pessoa/empresa vinculado ao depoimento).
      </p>

      <FormGroup>
        <Label>Stakeholder</Label>
        <Select
          classNamePrefix="select2-selection"
          isClearable
          isSearchable
          placeholder={stakeholderOptions.length ? 'Selecionar stakeholder...' : 'Nenhum stakeholder no projeto'}
          options={stakeholderOptions}
          value={depoimentoStake}
          onChange={opt => setDepoimentoStake(opt)}
          isDisabled={!stakeholderOptions.length}
        />
      </FormGroup>

      <FormGroup>
        <Label>Depoimento</Label>
        <Input
          type="textarea"
          rows={4}
          value={depoimentoTexto}
          onChange={e => setDepoimentoTexto(e.target.value)}
          placeholder="Texto do depoimento"
        />
        {depoimentoStake?.pf_nomeExibicao && (
          <div className="text-muted mt-1" style={{ fontSize: 12 }}>
            Associado a: <strong>{depoimentoStake.pf_nomeExibicao}</strong>
          </div>
        )}
      </FormGroup>

      <div className="d-flex justify-content-end mt-3">
        <Button color="primary" disabled={saving} onClick={handleSave}>
          {saving ? 'Salvando...' : 'Salvar Case'}
        </Button>
      </div>
    </div>
  )
}

export default MarketingCaseForm
